import { FC, useEffect, useState, useCallback } from "react";
import {
  Button,
  InputGroup,
  InputSelect,
  NO_REGEX,
  PhoneInputGroup,
  color,
} from "@funded-here-interface/common";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../styles/BizProfileCss";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import {
  countries,
  CountryCode,
  countryNameToCodeMap,
} from "@funded-here-interface/common/src/constant/country";
import {
  LegalStructures,
  IndustryGroupMap,
  LegalStructuresInvestor,
  LegalStructuresMerchantAndSPV,
} from "../../constants/dropdownValues";
import { Industry, IndustryGroup, LegalStructure } from "../../constants/enums";
import OnboardingLayout from "../../layouts/OnboardingLayout/OnboardingLayout";
import { Persona } from "@funded-here-interface/common/src/constant/enum";
import { useDisclosure } from "@mantine/hooks";
import InvestorDeclarationModal from "./Components/InvestorDeclarationModal/InvestorDeclarationModal";
import { toast } from "react-toastify";
import { camelCaseToNormalCase } from "../../Utils/formatter";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { initializeBusinessDetails } from "../../Utils/dispatchInstitutional";
import { formatDateDraft } from "@funded-here-interface/common/src/Utils/formatter";
import { isValidPhoneNumber } from "react-phone-number-input";

const initialFormState = {
  companyName: "",
  companyRegistrationNo: "",
  legalStructure: "",
  legalStructureOthers: "",
  countryOfIncorporation: "",
  countryOfDomicile: "",
  legalEntityAddress: "",
  postalCode: "",
  website: "",
  phoneNumber: "",
  industryGroup: "",
  industry: "",
  industryOthers: "",
  investmentAmount: 0,
  companyStartDate: "",
};

const initialErrorState = Object.keys(initialFormState).reduce(
  (acc, key) => {
    acc[key] = "";
    return acc;
  },
  {} as Record<string, string>
);

const BizProfile: FC = () => {
  const { classes } = useStyles();
  const Navigate = useNavigate();
  const type = useSelector((state: RootState) => state.auth.type);
  const isInvestor = type === Persona.INVESTOR;
  const investorDeclarationQualified = useSelector(
    (state: RootState) => state.biz.investorDeclarationQualified
  );
  const [opened, { close }] = useDisclosure(
    isInvestor && !investorDeclarationQualified
  );
  const dispatch = useDispatch();
  const userId = useSelector((state: RootState) => state.auth.userId);
  const { token } = useSelector((state: RootState) => state.auth);

  // Form state
  const [form, setForm] = useState({ ...initialFormState });
  const [error, setError] = useState({ ...initialErrorState });
  const [industryDropdownValues, setIndustryDropdownValues] = useState<
    Industry[]
  >([]);
  const [countryCode, setCountryCode] = useState<CountryCode | undefined>();
  const [initPage, setInitPage] = useState(true);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // Populate form from draft
  const populateFormFromDraft = useCallback((data: any) => {
    setForm({
      companyName: data.companyName || "",
      companyRegistrationNo: data.companyRegistrationNo || "",
      legalStructure: data.legalStructure || "",
      legalStructureOthers: data.legalStructureOthers || "",
      countryOfIncorporation: data.countryOfIncorporation || "",
      countryOfDomicile: data.countryOfDomicile || "",
      legalEntityAddress: data.legalEntityAddress || "",
      postalCode: data.postalCode ? String(data.postalCode) : "",
      website: data.website || "",
      phoneNumber: data.phoneNumber || "",
      industryGroup: data.industryGroup || "",
      industry: data.industry || "",
      industryOthers: data.industryOthers || "",
      investmentAmount: Number(data.investmentAmount) || 0,
      companyStartDate: formatDateDraft(data.companyStartDate),
    });

    if (data.countryOfDomicile) {
      const code = countryNameToCodeMap.get(data.countryOfDomicile);
      setCountryCode(code as CountryCode);
    }
    setIndustryDropdownValues(
      IndustryGroupMap[data.industryGroup as IndustryGroup] || []
    );
  }, []);

  // Fetch draft on mount
  useEffect(() => {
    const fetchDraft = async () => {
      if (initPage) {
        try {
          const params = { userId: parseInt(userId), token };
          const data = await getOrgDraftMutation.mutateAsync(params);
          if (data) populateFormFromDraft(data);
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };
    fetchDraft();
  }, [initPage, userId, token, populateFormFromDraft]);

  // Redirect if not qualified
  const investorDeclarationQualifiedSelector = useSelector(
    (state: RootState) => state.biz.investorDeclarationQualified
  );

  useEffect(() => {
    if (investorDeclarationQualifiedSelector === false) {
      Navigate("/unqualified-investor");
    }
  }, [investorDeclarationQualifiedSelector]);

  // Generic onChange for text fields
  const handleFieldChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError((prev) => ({ ...prev, [field]: "" }));
  };

  // Special handlers
  const handleLegalStructureChange = (value: LegalStructure) => {
    setForm((prev) => ({
      ...prev,
      legalStructure: value,
      legalStructureOthers: "",
    }));
    setError((prev) => ({
      ...prev,
      legalStructure: "",
      legalStructureOthers: "",
    }));
  };

  const handleIndustryGroupChange = (value: IndustryGroup) => {
    setForm((prev) => ({ ...prev, industryGroup: value, industry: "" }));
    setIndustryDropdownValues(IndustryGroupMap[value] || []);
    setError((prev) => ({ ...prev, industryGroup: "", industry: "" }));
  };

  const handleCountryOfDomicileChange = (value: string) => {
    setForm((prev) => ({ ...prev, countryOfDomicile: value }));
    setError((prev) => ({ ...prev, countryOfDomicile: "" }));
    const code = countryNameToCodeMap.get(value);
    setCountryCode(code as CountryCode);
  };

  // Validation
  const validateForm = () => {
    let valid = true;
    const customError: typeof error = { ...initialErrorState };

    // Custom error messages for specific fields
    const customMessages: Record<string, string> = {
      companyRegistrationNo: "Registration Number is required",
      companyStartDate: "Date of Incorporation is required",
      legalEntityAddress: "Address is required",
    };

    Object.entries(form).forEach(([key, value]) => {
      if (
        key !== "legalStructureOthers" &&
        key !== "industryOthers" &&
        key !== "investmentAmount" &&
        !value
      ) {
        valid = false;
        customError[key] =
          customMessages[key] || `${camelCaseToNormalCase(key)} is required`;
      }
    });
    if (isInvestor && !form.investmentAmount) {
      valid = false;
      customError.investmentAmount = "Planned Investment Amount is required";
    }

    if (!isValidPhoneNumber(String(form.phoneNumber))) {
      valid = false;
      customError.phoneNumber = "Phone Number is not valid";
    }
    setError(customError);
    return valid;
  };

  // Save draft
  const saveDraft = (showToast: boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        token,
        userId: parseInt(userId),
        ...form,
        companyPhoneNumber: form.phoneNumber,
        postalCode: form.postalCode || null,
        companyStartDate: form.companyStartDate ? form.companyStartDate : null,
      },
      {
        onSuccess: () => {
          if (showToast) toast.success("Draft Saved");
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  // Submit handler
  const handleSubmit = () => {
    if (validateForm()) {
      saveDraft(false);
      initializeBusinessDetails(dispatch, form, industryDropdownValues);

      if (isInvestor) {
        Navigate("/institutional-rep");
      } else {
        Navigate("/representative-details");
      }
    }
  };

  return (
    <OnboardingLayout
      step={1}
      showSteps={true}
      content={
        <>
          <InvestorDeclarationModal
            onError={toast.error}
            opened={opened}
            close={close}
          />
          <div style={{ marginTop: "70px" }}>
            <InputGroup
              isErrored={error.companyName}
              errorMessage
              onChange={(value: string) =>
                handleFieldChange("companyName", value)
              }
              labelText="Company Name"
              id="companyName"
              value={form.companyName}
            />
            <InputGroup
              isErrored={error.companyRegistrationNo}
              errorMessage
              onChange={(value: string) =>
                handleFieldChange("companyRegistrationNo", value)
              }
              labelText="Registration Number"
              id="registrationNumber"
              value={form.companyRegistrationNo}
            />
            <InputGroup
              isErrored={error.companyStartDate}
              errorMessage
              onChange={(value: any) =>
                handleFieldChange("companyStartDate", value)
              }
              labelText="Date of Incorporation"
              value={form.companyStartDate}
              id="incorporationDate"
              type="date"
            />
            <InputSelect
              isErrored={error.legalStructure}
              errorMessage
              onChange={handleLegalStructureChange}
              labelText="Legal Structure"
              id="legalStructure"
              value={form.legalStructure}
              data={
                !isInvestor
                  ? LegalStructuresMerchantAndSPV
                  : LegalStructuresInvestor
              }
            />
            <InputSelect
              isErrored={error.industryGroup}
              errorMessage
              onChange={handleIndustryGroupChange}
              labelText="Industry Group"
              value={form.industryGroup}
              id="industryGroup"
              data={Object.values(IndustryGroup).sort((a, b) =>
                a.localeCompare(b)
              )}
            />
            <InputSelect
              isErrored={error.industry}
              errorMessage
              onChange={(value: Industry) =>
                handleFieldChange("industry", value)
              }
              labelText="Industry"
              value={form.industry}
              id="industry"
              data={[...industryDropdownValues].sort((a, b) =>
                a.localeCompare(b)
              )}
            />
            <InputSelect
              isErrored={error.countryOfIncorporation}
              errorMessage
              onChange={(value: string) =>
                handleFieldChange("countryOfIncorporation", value)
              }
              labelText="Country of Incorporation"
              id="countryOfIncorporation"
              value={form.countryOfIncorporation}
              data={countries}
            />
            <InputSelect
              isErrored={error.countryOfDomicile}
              errorMessage
              onChange={handleCountryOfDomicileChange}
              labelText="Country of Domicile"
              id="countryOfDomicile"
              value={form.countryOfDomicile}
              data={countries}
            />
            <InputGroup
              isErrored={error.legalEntityAddress}
              errorMessage
              onChange={(value: string) =>
                handleFieldChange("legalEntityAddress", value)
              }
              labelText="Address"
              id="address"
              value={form.legalEntityAddress}
            />
            <InputGroup
              isErrored={error.postalCode}
              errorMessage
              onChange={(value: string) =>
                handleFieldChange("postalCode", value.replace(NO_REGEX, ""))
              }
              labelText="Postal Code"
              id="postalcode"
              value={form.postalCode}
            />
            <InputGroup
              isErrored={error.website}
              errorMessage
              onChange={(value: string) => handleFieldChange("website", value)}
              labelText="Website"
              id="companywebsite"
              value={form.website}
            />
            {isInvestor && (
              <InputGroup
                isErrored={error.investmentAmount}
                errorMessage
                onChange={(value: string) =>
                  handleFieldChange(
                    "investmentAmount",
                    parseInt(value.replace(NO_REGEX, "")) || 0
                  )
                }
                labelText="Planned Investment Amount"
                id="investmentAmount"
                value={`${form.investmentAmount || 0}`}
              />
            )}
            <div className={classes.phoneNumberWrapper}>
              <PhoneInputGroup
                isErrored={error.phoneNumber}
                value={form.phoneNumber}
                country={countryCode}
                onChange={(value: string) =>
                  handleFieldChange("phoneNumber", value)
                }
              />
            </div>
            <div className={classes.buttonWrapper}>
              <Button
                onClick={handleSubmit}
                textColor={color.WHITE}
                backgroundColor={color.FHGREEN}
                width="100%"
              >
                Next
              </Button>
            </div>
            <div className={classes.buttonWrapper}>
              <Button
                onClick={() => saveDraft()}
                textColor={color.WHITE}
                backgroundColor={color.LOANTEXT}
                width="100%"
              >
                Save Draft
              </Button>
            </div>
          </div>
        </>
      }
    />
  );
};

export default BizProfile;
